'use client';

import { useEffect, useState, createContext, useContext } from 'react';
import { createClient } from './client';
import type { User } from '@supabase/supabase-js';
import { sendGTMEvent } from '@next/third-parties/google';
import { useRouter } from 'next/navigation';
import nukeCookies from './nukeCookies';
import { usePostHog } from 'posthog-js/react';

type UserContextType = {
  supabase: ReturnType<typeof createClient>;
  user: User | null;
  contact: Contact | null;
  userDetails?: UserDetails | null;
  newsletters?: Newsletter[];
  refreshUser: () => void;
  signOut: () => void;
};

export const UserContext = createContext<UserContextType | undefined>(
  undefined
);

export type Props = {
  user: User | null;
  children: React.ReactNode;
  newsletters?: Newsletter[];
  contact: Contact | null;
};

const fetchContact = async () => {
  const response = await fetch('/api/contact', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });
  const data = (await response.json()) as Contact;
  return data;
};

const mapNewslettersToArray = (newsletters: Record<string, boolean>) => {
  const newsletterArray = [];
  for (const [key, value] of Object.entries(newsletters)) {
    if (value) {
      newsletterArray.push(key);
    }
  }
  return newsletterArray;
};

export const UserContextProvider = ({
  user: initialUser,
  contact: initialContact,
  newsletters,
  children
}: Props) => {
  const posthog = usePostHog();
  const [user, setUser] = useState<User | null>(initialUser);
  const [contact, setContact] = useState<Contact | null>(initialContact);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [supabase] = useState(() => createClient());
  const router = useRouter();

  // sign up to listen to changes in user_roles table
  useEffect(() => {
    const roleChange = supabase
      .channel('supabase_realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_roles'
        },
        (_payload) => {
          void supabase.auth.refreshSession();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_roles'
        },
        (_payload) => {
          void supabase.auth.refreshSession();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'user_roles'
        },
        (_payload) => {
          console.log('refreshing session');
          void supabase.auth.refreshSession();
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(roleChange);
    };
  }, [supabase]);

  useEffect(() => {
    if (user)
      posthog.identify(user.id, {
        email: user.email
      });
  }, [posthog, user]);

  useEffect(() => {
    if (initialUser) {
      sendGTMEvent({
        user_id: initialUser?.id ?? null
      });
    }

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      sendGTMEvent({
        user_id: session?.user?.id ?? null
      });
      if (event === 'SIGNED_IN') {
        // console.log('signed in');
        setUser(session?.user ?? null);
      } else if (event === 'SIGNED_OUT') {
        // console.log('signed out');
        setUser(null);
        // window.location.href = "/";
      } else if (event == 'PASSWORD_RECOVERY') {
        const newPassword = prompt(
          'What would you like your new password to be?'
        );
        if (newPassword) {
          const { data, error } = await supabase.auth.updateUser({
            password: newPassword
          });
          if (data) alert('Password updated successfully!');
          if (error) alert('There was an error updating your password.');
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [initialUser, router, supabase]);

  useEffect(() => {
    sendGTMEvent({
      user_id: user?.id,
      user_email: contact?.email,
      user_member_level: contact?.membership_level,
      user_membership_status: contact?.membership_status,
      user_newsletters: contact?.mailchimp_interests
        ? mapNewslettersToArray(
            contact.mailchimp_interests as unknown as Record<string, boolean>
          )
        : null,
      user_interests: contact?.interests,
      user_city: contact?.city,
      user_industry: contact?.industry,
      user_position: contact?.position
    });
  }, [user, contact]);

  useEffect(() => {
    if (user) {
      void fetchContact().then((contact) => {
        setContact(contact);
      });
    }
  }, [user]);

  useEffect(() => {
    if (contact) {
      const details: UserDetails = {
        id: contact.id,
        email: contact.email ?? '',
        firstName: contact.first_name ?? '',
        lastName: contact.last_name ?? '',
        mobile: contact.mobile ?? '',
        city: contact.city ?? '',
        company: contact.company ?? '',
        position: contact.position ?? '',
        industry: contact.industry ?? '',
        website: contact.website ?? '',
        twitter: contact.twitter ?? '',
        instagram: contact.instagram ?? '',
        interests: contact.interests ? (contact.interests as string[]) : []
      };
      setUserDetails(details);
    } else {
      setUserDetails(null);
    }
  }, [contact]);

  const value = {
    contact,
    userDetails,
    supabase,
    user,
    newsletters,
    refreshUser: () => {
      const refreshSession = async () => {
        const supabase = createClient();
        // const { data } = await supabase.auth.refreshSession();
        // const { user } = data;
        const {
          data: { user }
        } = await supabase.auth.getUser();
        setUser(user);
      };
      void refreshSession();
    },
    signOut: async () => {
      await supabase.auth.signOut();
      nukeCookies();
      sendGTMEvent({
        event: 'sign_out'
        // userId: null
      });
    }
  };
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error(`useUser must be used within a UserContextProvider.`);
  }
  return context;
};
